import {
    Column,
    Container,
    DropDown,
    Grid,
    Header,
    IconButton,
    Link,
    Row,
    SearchIcon,
    Separator,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const StyledLink = styled(RouterLink)`
  text-decoration: none;
  color: black;

  ${(props) =>
      props.$isActive &&
      `
    pointer-events: none;
    color: gray;
  `}
`;

// Matches the DCW search page header's vertical breathing room around the title.
const HeaderWrap = styled.div`
  margin: 3rem 0;
`;

const PoliciesHeader = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const location = useLocation();
    const intl = useIntl();
    const screen = useScreen();
    const { pathname } = location;
    const query = props.query.board;
    const basePath = '/policies';

    // The List/Incomplete/Bound tabs use the shared FilterableList + its collapse toggle; Board has
    // its own dropdowns instead.
    const isFilterableTab =
        pathname === basePath || pathname === `${basePath}/incomplete` || pathname === `${basePath}/bound`;

    // Get translated texts
    const policiesTitle = intl.formatMessage({ id: 'Policies_Header.title' });

    // Navigation links
    const allText = intl.formatMessage({ id: 'Policies_Header.nav.all' });
    const incompleteText = intl.formatMessage({ id: 'Policies_Header.nav.incomplete' });
    const boundText = intl.formatMessage({ id: 'Policies_Header.nav.bound' });
    const boardText = intl.formatMessage({ id: 'Policies_Header.nav.board' });

    // Filter dropdowns
    const typeText = intl.formatMessage({ id: 'Policies_Header.filter.type' });
    const allTransactionTypeText = intl.formatMessage({ id: 'Policies_Board.filter.allTransactionType' });
    const newBusinessesText = intl.formatMessage({ id: 'Policies_Board.filter.newBusinesses' });
    const brokerageText = intl.formatMessage({ id: 'Policies_Header.filter.brokerage' });
    const assignedToText = intl.formatMessage({ id: 'Policies_Header.filter.assignedTo' });

    // Define transaction type options with translations
    const transactionTypeOptions = [
        { code: null, value: allTransactionTypeText },
        { code: 'new', value: newBusinessesText },
    ];

    return (
        <HeaderWrap>
            <Grid>
                <Row>
                    {screen?.isMobile && (
                        <Column>
                            <Container fullSize verticalAlignment={alignments.bottom}>
                                <Header size={sizes.lg} text={policiesTitle} top={sizes.xs} />
                            </Container>
                        </Column>
                    )}
                    <Column xs={12} md={3}>
                        <Container inline horizontalAlignment={alignments.left} top={sizes.sm}>
                            <StyledLink to={basePath} $isActive={pathname === basePath}>
                                <Link text={allText} right={sizes.xs} disabled={pathname === basePath} />
                            </StyledLink>
                            <StyledLink to={`${basePath}/incomplete`} $isActive={pathname === `${basePath}/incomplete`}>
                                <Link
                                    text={incompleteText}
                                    right={sizes.xs}
                                    disabled={pathname === `${basePath}/incomplete`}
                                />
                            </StyledLink>
                            <StyledLink to={`${basePath}/bound`} $isActive={pathname === `${basePath}/bound`}>
                                <Link text={boundText} right={sizes.xs} disabled={pathname === `${basePath}/bound`} />
                            </StyledLink>
                            <StyledLink to={`${basePath}/board`} $isActive={pathname === `${basePath}/board`}>
                                <Link text={boardText} right={sizes.xs} disabled={pathname === `${basePath}/board`} />
                            </StyledLink>
                        </Container>
                    </Column>
                    {!screen?.isMobile && (
                        <Column md={6}>
                            <Container fullSize verticalAlignment={alignments.bottom}>
                                <Header size={sizes.xl} text={policiesTitle} />
                            </Container>
                        </Column>
                    )}
                    <Column xs={12} md={3}>
                        <Container
                            fullSize
                            inline
                            horizontalAlignment={alignments.right}
                            verticalAlignment={alignments.center}
                            top={screen?.isMobile && sizes.xs}
                        >
                            {isFilterableTab && (
                                <IconButton
                                    plain
                                    icon={<SearchIcon />}
                                    dot={!props.showFilters && props.hasActiveFilters}
                                    clickEvent={() => props.setShowFilters((value) => !value)}
                                />
                            )}
                            {props.view === 'board' && (
                                <React.Fragment>
                                    <Container right={sizes.xs}>
                                        <DropDown
                                            placeholder={typeText}
                                            value={query && { code: query.transactionType }}
                                            references={transactionTypeOptions}
                                            changeEvent={(value) =>
                                                props.doReduceQueryElement({
                                                    code: 'transactionType',
                                                    value: value.code,
                                                })
                                            }
                                            allowEdit
                                        />
                                    </Container>

                                    {props.context.session.userType === 'administrator' && (
                                        <DropDown
                                            placeholder={brokerageText}
                                            value={query && { code: query.brokerCompanyReferenceGuid }}
                                            references={props.brokerCompanies}
                                            referencesEvent={() =>
                                                props.reference.doGetReferences('ActiveBrokerCompanies')
                                            }
                                            changeEvent={(value) =>
                                                props.doReduceQueryElement({
                                                    code: 'brokerCompanyReferenceGuid',
                                                    value: value.code,
                                                })
                                            }
                                            allowEdit
                                        />
                                    )}

                                    {props.context.session.userType === 'broker' && (
                                        <DropDown
                                            placeholder={assignedToText}
                                            value={query && { code: query.brokerReferenceGuid }}
                                            references={props.assignees}
                                            referencesEvent={() => props.reference.doGetReferences('ActiveBrokers')}
                                            changeEvent={(value) =>
                                                props.doReduceQueryElement({
                                                    code: 'brokerReferenceGuid',
                                                    value: value.code,
                                                })
                                            }
                                            allowEdit
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </Container>
                    </Column>
                </Row>
                <Row noguttertop>
                    <Column>
                        <Separator solid />
                    </Column>
                </Row>
            </Grid>
        </HeaderWrap>
    );
};

export default PoliciesHeader;
